/**
 * Utilities for working with payment data
 */

/**
 * Format payment state to a human-readable status
 * @param state Payment state from API
 * @returns Formatted status string
 */
export const formatPaymentState = (state: string): string => {
    switch (state.toUpperCase()) {
        case 'CONFIRMED':
            return 'Confirmed';
        case 'PENDING':
            return 'Pending';
        case 'FAILED':
            return 'Failed';
        case 'REFUNDED':
            return 'Refunded';
        case 'PARTIALLY_REFUNDED':
            return 'Partially Refunded';
        default:
            return state;
    }
};

/**
 * Format currency amount with currency symbol
 * @param amount Amount value
 * @param currency Currency code (e.g., USD)
 * @returns Formatted currency string
 */
export const formatCurrency = (amount: number, currency: string = 'USD'): string => {
    // Use the Intl.NumberFormat for proper currency formatting
    return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
    }).format(amount);
};

/**
 * Get appropriate payment type label based on payment data
 * @param payment Payment data
 * @returns Payment type string
 */
export const getPaymentType = (payment: {
    shipmentOrderId?: string;
    combinedPackageId?: number;
    packageId?: number;
}): string => {
    if (payment.shipmentOrderId) return 'Shipment Payment';
    if (payment.combinedPackageId) return 'Combined Package Payment';
    if (payment.packageId) return 'Package Payment';
    return 'Payment';
};

/**
 * Truncate transaction ID for display
 * @param transactionId Full transaction ID
 * @param length Length to display (default: 8)
 * @returns Truncated transaction ID
 */
export const truncateTransactionId = (transactionId: string, length: number = 8): string => {
    if (transactionId.length <= length * 2) {
        return transactionId;
    }
    return `${transactionId.substring(0, length)}...${transactionId.substring(transactionId.length - 4)}`;
};

/**
 * Format a date to a human-readable string using native JavaScript
 * @param date Date to format
 * @param format Format type: 'short' (MMM d, yyyy) or 'long' (full date and time)
 * @returns Formatted date string
 */
export const formatDate = (date: Date, format: 'short' | 'long' = 'short'): string => {
    const options: Intl.DateTimeFormatOptions = format === 'short'
        ? { year: 'numeric', month: 'short', day: 'numeric' }
        : {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit'
        };

    return date.toLocaleDateString('en-US', options);
};
